<script>
    import moment from 'moment';
    import {network, store} from '../stores.js';
    import TopMenu from '../components/TopMenu.svelte';
    import {Link, navigate} from 'svelte-routing';
    import BottomMenu from '../components/BottomMenu.svelte';
    import {getStatus, isNewOrder, isCorrectedComment} from '../lib/orderStatus.js';


    export let id;
    export let vid;

    const user = store('credential', 'getAuth');
    const axis = store('axis', '', false);

    const isIdMissing = (v) => v == null || v === '' || v === 'undefined' || v === 'null';
    $: idMissing = isIdMissing(id);

    const toArray = (data) => {
        if (!data) return [];
        return Array.isArray(data) ? data : [data];
    };

    const orderKey = (item) => String(item?.nomer ?? item?.nomDoc ?? '');

    const hasValue = (v) => v != null && v !== '';

    const firstNonEmpty = (...vals) => vals.find((v) => hasValue(v)) ?? null;

    const formatDate = (v) => {
        if (!hasValue(v)) return null;
        const m = moment(v);
        return m.isValid() ? m.format('DD.MM.YYYY') : String(v);
    };

    const formatMoney = (v) => {
        if (v == null || v === '') return null;
        return `${(+v).toFixed(0)} ₽`;
    };

    const isHttpUrl = (s) => /^https?:\/\//i.test(s);

    /** Delimited file names/URLs → link list; otherwise plain text. */
    const parseFiles = (files) => {
        if (!hasValue(files)) return null;
        const str = String(files).trim();
        if (!str) return null;

        let parts = str.split(/[;\n|]+/).map((s) => s.trim()).filter(Boolean);
        if (parts.length <= 1 && str.includes(',')) {
            const byComma = str.split(',').map((s) => s.trim()).filter(Boolean);
            if (byComma.length > 1) parts = byComma;
        }

        if (parts.length > 1 || parts.some(isHttpUrl)) {
            return {
                type: 'files',
                items: parts.map((p) => ({
                    href: isHttpUrl(p) ? p : null,
                    label: p.replace(/^.*[\\/]/, '') || p,
                    text: p,
                })),
            };
        }

        return {type: 'text', value: str};
    };

    const executorName = (item) =>
        firstNonEmpty(item?.kontrName, item?.kontrUrName, item?.name, item?.initiatName);

    const kontragentName = (item) =>
        firstNonEmpty(item?.kontrName, item?.kontrUrName, item?.name);

    /**
     * Curated detail rows (legacy ExtJS grid labels).
     * komment is shown once in the top block as «Комментарий».
     */
    const buildDetailRows = (item) => {
        if (!item) return [];

        const rows = [];
        const push = (label, value) => {
            if (value == null || value === '') return;
            rows.push({label, value, type: 'text'});
        };
        const pushDate = (label, raw) => {
            const formatted = formatDate(raw);
            if (formatted) rows.push({label, value: formatted, type: 'text'});
        };


        pushDate('Дата создания', item.data);

        {
            const money = formatMoney(item.summa);
            if (money) rows.push({label: 'Сумма', value: money, type: 'text'});
        }
        push('Плательщик', item.urlicoName);
        push('Счет/касса', item.mestoDSName);
        push('Пользователь', item.username);
        push('Контрагент', kontragentName(item));
        // «Комментарий» (komment) — only in top block

        push('Аналитика', item.exp2);
        push('Отдел', item.dep);
        push('Проект', item.proekt);

        push('№ п/п', item.nomPP);
        pushDate('Дата п/п', item.datePP);
        push('№ счета', item.nomSchet);
        pushDate('Дата счета', item.dateSchet);
        push('Инициатор', item.initiatName);

        const files = parseFiles(item.files);
        if (files) {
            if (files.type === 'files') {
                rows.push({label: 'Прикрепленные файлы', value: files.items, type: 'files'});
            } else {
                rows.push({label: 'Прикрепленные файлы', value: files.value, type: 'text'});
            }
        }

        if (hasValue(item.kommentKontr)) {
            rows.push({
                label: 'Комментарий контролёра',
                value: String(item.kommentKontr).trim(),
                type: 'rejection',
                vid: 'kontr',
                corrected: isCorrectedComment(item.kommentKontr),
            });
        }
        if (hasValue(item.kommentKazna)) {
            rows.push({
                label: 'Комментарий казначея',
                value: String(item.kommentKazna).trim(),
                type: 'rejection',
                vid: 'kazna',
                corrected: isCorrectedComment(item.kommentKazna),
            });
        }

        return rows;
    };

    // Normalize finance row from planDvizhDeneg / oplatDvizhDenegApp into the shape the template expects
    const normalizeFinOrder = (row) => {
        if (!row) return null;
        return {
            ...row,
            nomer: row.nomer ?? row.nomDoc ?? id,
            nomDoc: row.nomDoc ?? row.nomer ?? id,
            vid: row.vid ?? vid,
            tip: row.tip,
            flag1: row.flag1,
            komment: row.komment,
            kontrName: row.kontrName,
            kontrUrName: row.kontrUrName,
            name: row.name,
            initiatName: row.initiatName,
            proekt: row.proekt,
            summa: row.summa,
            summaOpl: row.summaOpl,
            data: row.data,
            flag: row.flag,
        };
    };

    const fetchOrder = async () => {
        if (isIdMissing(id)) return null;

        const date1 = moment().subtract(1, 'year').format('YYYY-MM-DD');
        const date2 = moment().add(1, 'days').format('YYYY-MM-DD');


        const [planRaw, oplatRaw] = await Promise.all([
            network.getPlanListRange(+$user.id),
            network.getPayListRange(+$user.id, date1, date2),
        ]);

        const planList = toArray(planRaw);
        const oplatList = toArray(oplatRaw);


        const list = [...planList, ...oplatList];
        const found = list.find((item) => orderKey(item) === String(id));

        if (!found) {

            return {order: null, tv: [], kontakti: [], source: 'finance'};
        }

        const order = normalizeFinOrder(found);


        return {
            order,
            tv: [],
            kontakti: [],
            source: 'finance',
        };
    };
    let order = fetchOrder();

    let fixingVid = null;
    let fixError = null;

    const markFixed = async (noteVid) => {
        if (fixingVid) return;
        let data = await order;
        if (!data?.order) return;

        fixingVid = noteVid;
        fixError = null;
        try {
            await network.toChek(data.order.nomer, 5, noteVid);
            order = fetchOrder();
            await order;
            const el = document.getElementById('modal-fixed');
            if (el && typeof bootstrap !== 'undefined') {
                const popup = new bootstrap.Modal(el);
                popup.show();
                setTimeout(() => popup.hide(), 1800);
            }
        } catch (e) {
            console.error(e);
            fixError = 'Не удалось отметить исправление. Попробуйте ещё раз.';
        } finally {
            fixingVid = null;
        }
    };

    const fetchChatMessagesCount = async () => {
        if (isIdMissing(id)) return 0;
        let buf = await network.getChatMessagesCount(id, +$user.id);
        return buf && buf[0] && buf[0].count ? +buf[0].count : 0;
    }
    let chat = fetchChatMessagesCount();

    // === Смена статуса через viborkaTo ===
    const API_URL = 'https://api.trademaster.pro/proxy?apikey=7ofsUxeIiti8wQ8Rlvhteqm%2BjG1MkRD%2BdLlJfW9BxOWIyLUPIs8FueQ6IIa%2F';

    // vid → newVid маппинг из десктопной логики (Uchet-2.js otpralen/fromDraftViborkaTo)
    // Статусы: 16=КП, 17=В оценку, 18=Согласование, 19=Ожидание инфо, 20=В работу, 21=Выполненоeab308

    /* { newVid: 17, label: 'В получение данных', icon: 'download-outline',      color: '#28c6dc' },
     { newVid: 18, label: 'В оценку',           icon: 'search-outline',         color: '#bf48bd' },
     { newVid: 19, label: 'В согласование',     icon: 'checkmark-done-outline', color: '#a855f7' },
     { newVid: 20, label: 'Ожидание информации',icon: 'time-outline',           color: '#f97316' },*/

    const statuses = [

        {newVid: 21, label: 'В работу', icon: 'construct-outline', color: '#22c55e'},
    ];

    let statusLoading = null;
    let statusError = null;
    let statusSuccess = null;

    const handlerChangeStatus = async (newVid, statusLabel) => {
        statusLoading = newVid;
        statusError = null;
        statusSuccess = null;

        try {
            let data = await order;
            if (!data?.order) throw new Error('order missing');
            const nom = data.order.nomer;
            const vid = data.order.vid;
            const nomStr = data.order.nomerStr || String(nom);

            const query = `EXEC viborkaTo @nom = ${nom}, @vid = ${vid}, @newVid = ${newVid}`;

            const params = new URLSearchParams({query, one: 'false'});
            const res = await fetch(`${API_URL}&${params.toString()}`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            statusSuccess = statusLabel;
            // обновить данные заявки
            order = fetchOrder();

            network.sendChatMessage(nom, +$user.id, $user.name, 'Перевод в "' + statusLabel + '"', 'text');

            let popup = new bootstrap.Modal(document.getElementById('modal-status'));
            popup.show();
            setTimeout(() => popup.hide(), 1800);
        } catch (e) {
            console.error(e);
            statusError = 'Не удалось сменить статус. Попробуйте ещё раз.';
        } finally {
            statusLoading = null;
        }
    }

    let confirmMessage = '';
    let confirmModalInstance = null;
    let resolveConfirmPromise = null;

    const resolveConfirm = (result) => {
        if (resolveConfirmPromise) {
            resolveConfirmPromise(result);
            resolveConfirmPromise = null;
        }
        confirmModalInstance?.hide();
    };

    const confirmDialog = (message) => {
        confirmMessage = message;
        return new Promise((resolve) => {
            resolveConfirmPromise = resolve;
            confirmModalInstance = new bootstrap.Modal(document.getElementById('modal-confirm-delete'));
            confirmModalInstance.show();
        });
    };

    let deleteLoading = false;
    let deleteError = null;

    const handlerDeleteOrder = async () => {
        deleteLoading = true;
        deleteError = null;

        try {
            let data = await order;

            const confirmed = await confirmDialog(
                'Удалить заявку #' + data.order.nomer + '? Действие необратимо.'
            );
            if (!confirmed) {
                deleteLoading = false;
                return;
            }

            await network.deleteOrder(data.order.nomer, 5);

            console.log('Delete order stub called for', data.order.nomer, 5);

            navigate('/dashboard', {replace: true});
        } catch (e) {
            console.error(e);
            deleteError = 'Не удалось удалить заявку. Попробуйте ещё раз.';
        } finally {
            deleteLoading = false;
        }
    };
</script>

<style>
    /* === Блок смены статуса === */
    .status-actions {
        margin: 12px 16px 8px;
        background: #f8f9fa;
        border: 1px solid #e4e6ea;
        border-radius: 10px;
        overflow: hidden;
    }

    .status-actions-title {
        font-size: 11px;
        font-weight: 600;
        color: #9ca3af;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 10px 14px 6px;
    }

    .status-btn-list {
        list-style: none;
        margin: 0;
        padding: 0 0 6px;
    }

    .status-btn-item {
        padding: 2px 8px;
    }

    .status-btn {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 9px 12px;
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #374151;
        cursor: pointer;
        transition: background 0.15s, transform 0.15s;
        text-align: left;
        border-left-width: 3px;
    }

    .status-btn:active:not(:disabled) {
        transform: scale(0.98);
    }

    .status-btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    .status-btn ion-icon {
        font-size: 18px;
        flex-shrink: 0;
    }

    .btn-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid #d1d5db;
        border-top-color: #6b7280;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
        flex-shrink: 0;
    }

    .status-error {
        font-size: 12px;
        color: #dc2626;
        padding: 4px 14px 8px;
    }

    .file-list {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .file-list li + li {
        margin-top: 4px;
    }

    .file-list a {
        word-break: break-all;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }


    .status-actions-danger {
        border-color: #fca5a5;
    }

    .status-btn-danger {
        color: #dc2626;
        border-left-color: #dc2626;
    }

    .status-btn-danger:active:not(:disabled) {
        background: #fef2f2;
    }

    .status-btn-edit {
        color: #0b7285;
        border-left-color: #0b7285;
    }

    .status-btn-edit:active:not(:disabled) {
        background: #f0f7f8;
    }

    .detail-row--rejected {
        background: #fef2f2;
    }

    .detail-row--corrected {
        background: #ecfdf3;
    }

    .rejection-detail {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        flex-wrap: wrap;
    }

    .rejection-detail b {
        flex: 1;
        min-width: 0;
        white-space: pre-wrap;
    }

    .rejection-fix-btn {
        flex-shrink: 0;
        padding: 5px 12px;
        border-radius: 999px;
        border: 1px solid #16a34a;
        background: #fff;
        color: #15803d;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
    }

    .rejection-fix-btn:disabled {
        opacity: 0.6;
        cursor: wait;
    }
</style>

<TopMenu title="{idMissing ? 'Заявка не найдена' : `Детали заявки #${id}`}">
    <Link to="/dashboard" class="headerButton goBack" slot="left">
        <ion-icon name="chevron-back-outline" role="img" class="md hydrated"
                  aria-label="chevron back outline"></ion-icon>
    </Link>
    {#if !idMissing}
        <Link to="/chat/{id}" class="headerButton" slot="right">
            <ion-icon name="mail-outline" role="img" class="md hydrated" aria-label="mail outline"></ion-icon>

            {#await chat}...
            {:then count}
                {#if count}
                    <span class="badge badge-danger">{count}</span>
                {/if}
            {/await}
        </Link>
    {/if}
</TopMenu>

{#if idMissing}
    <div id="appCapsule">
        <div class="section mt-2">
            <div class="card text-center">
                <div class="card-body p-5">
                    <h5 class="card-title">Заявка не найдена</h5>
                    <p class="card-text mb-3">Некорректный или отсутствующий номер заявки.</p>
                    <Link to="/dashboard" class="btn btn-primary">Назад</Link>
                </div>
            </div>
        </div>
    </div>
{:else}


    <div id="appCapsule">
        {#await order}
            <div class="section mt-2">
                <div class="card text-center">
                    <div class="card-body p-5">
                        <h5 class="card-title">Загрузка</h5>
                        <p class="card-text">
                            Пожалуйста подождите, мы загружаем информацию по вашей заявке..
                        </p>
                    </div>
                </div>
            </div>
        {:then data}
            {#if !data?.order}
                <div class="section mt-2">
                    <div class="card text-center">
                        <div class="card-body p-5">
                            <h5 class="card-title">Заявка не найдена</h5>
                            <p class="card-text mb-3">Не удалось загрузить данные заявки #{id}.</p>
                            <Link to="/dashboard" class="btn btn-primary">Назад</Link>
                        </div>
                    </div>
                </div>
            {:else}


                <div class="mt-2">
                    <ul class="listview">
                        {#if hasValue(data.order.nomDoc)}
                            <li>
                                <div class="in">
                                    <div>
                                        <header>Заявка</header>
                                        <b>{data.order.nomDoc}</b>
                                    </div>
                                </div>
                            </li>
                        {/if}
                        {#each [getStatus(data.order)] as status}
                            {#if status}
                                <li>
                                    <div class="in">
                                        <div>
                                            <header>Статус</header>
                                            <b style="color: {status.color}">{status.label}</b>
                                        </div>
                                    </div>
                                </li>
                            {/if}
                        {/each}
                        {#if hasValue(data.order.komment)}
                            <li>
                                <div class="in">
                                    <div>
                                        <header>Комментарий</header>
                                        <b>{data.order.komment}</b>
                                    </div>
                                </div>
                            </li>
                        {/if}
                        {#if executorName(data.order)}
                            <li>
                                <div class="in">
                                    <div>
                                        <header>Контрагент</header>
                                        <b>{executorName(data.order)}</b>
                                    </div>
                                </div>
                            </li>
                        {/if}
                        {#each buildDetailRows(data.order) as row (row.label)}
                            <li
                                class:detail-row--rejected={row.type === 'rejection' && !row.corrected}
                                class:detail-row--corrected={row.type === 'rejection' && row.corrected}
                            >
                                <div class="in">
                                    <div>
                                        <header>{row.label}</header>
                                        {#if row.type === 'files'}
                                            <ul class="file-list">
                                                {#each row.value as file}
                                                    <li>
                                                        {#if file.href}
                                                            <a href={file.href} target="_blank"
                                                               rel="noopener noreferrer"><strong>{file.label}</strong></a>
                                                        {:else}
                                                            <strong>{file.text}</strong>
                                                        {/if}
                                                    </li>
                                                {/each}
                                            </ul>
                                        {:else if row.type === 'rejection'}
                                            <div class="rejection-detail">
                                                <b>{row.value}</b>
                                                {#if !row.corrected}
                                                    <button
                                                            type="button"
                                                            class="rejection-fix-btn"
                                                            disabled={fixingVid === row.vid}
                                                            on:click={() => markFixed(row.vid)}
                                                    >
                                                        {fixingVid === row.vid ? '…' : 'Исправлено'}
                                                    </button>
                                                {/if}
                                            </div>
                                        {:else}
                                            <b>{row.value}</b>
                                        {/if}
                                    </div>
                                </div>
                            </li>
                        {/each}
                    </ul>
                    {#if fixError}
                        <div class="status-error">{fixError}</div>
                    {/if}

                    {#if isNewOrder(data?.order) }
                        <div class="status-actions ">
                            <ul class="status-btn-list">
                                <li class="status-btn-item">
                                    <button
                                            type="button"
                                            class="status-btn status-btn-edit"
                                            on:click={() => navigate(`/edit/${data.order.nomer}/${data.order.vid ?? vid}`)}
                                    >
                                        <ion-icon name="create-outline"></ion-icon>
                                        Редактировать
                                    </button>
                                </li>
                                <li class="status-btn-item">
                                    <button
                                            class="status-btn status-btn-danger"
                                            on:click={handlerDeleteOrder}
                                            disabled={deleteLoading}
                                    >
                                        {#if deleteLoading}
                                            <span class="btn-spinner"></span>
                                        {:else}
                                            <ion-icon name="trash-outline" style="color: #dc2626;"></ion-icon>
                                        {/if}
                                        Удалить заявку
                                    </button>
                                </li>
                            </ul>
                            {#if deleteError}
                                <div class="status-error">{deleteError}</div>
                            {/if}
                        </div>
                    {/if}
                    <!-- Модалка: статус изменён -->
                    <div class="modal fade dialogbox" id="modal-status" data-bs-backdrop="static" tabindex="-1"
                         style="display: none;" aria-hidden="true">
                        <div class="modal-dialog" role="document">
                            <div class="modal-content">
                                <div class="modal-icon text-success">
                                    <ion-icon name="checkmark-circle"></ion-icon>
                                </div>
                                <div class="modal-header">
                                    <h5 class="modal-title">Статус изменён</h5>
                                    {#if statusSuccess}
                                        <p class="modal-subtitle">{statusSuccess}</p>
                                    {/if}
                                </div>
                                <div class="modal-footer">
                                    <div class="btn-inline">
                                        <a href="#" class="btn" data-bs-dismiss="modal">Закрыть</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Модалка: подтверждение удаления -->
                    <div class="modal fade dialogbox" id="modal-confirm-delete" data-bs-backdrop="static" tabindex="-1"
                         style="display: none;" aria-hidden="true">
                        <div class="modal-dialog" role="document">
                            <div class="modal-content">
                                <div class="modal-icon text-danger">
                                    <ion-icon name="alert-circle-outline"></ion-icon>
                                </div>
                                <div class="modal-header">
                                    <h5 class="modal-title">{confirmMessage}</h5>
                                </div>
                                <div class="modal-footer">
                                    <div class="btn-inline">
                                        <a href="#" class="btn"
                                           on:click|preventDefault={() => resolveConfirm(false)}>Отмена</a>
                                        <a href="#" class="btn btn-danger"
                                           on:click|preventDefault={() => resolveConfirm(true)}>Удалить</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            {/if}
        {/await}
    </div>
    <div class="modal fade dialogbox" id="modal-fixed" data-bs-backdrop="static" tabindex="-1"
         aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-icon text-success">
                    <ion-icon name="checkmark-circle"></ion-icon>
                </div>
                <div class="modal-header">
                    <h5 class="modal-title">Исправлено</h5>
                    <p class="modal-subtitle">Замечание отмечено как исправленное</p>
                </div>
                <div class="modal-footer">
                    <div class="btn-inline">
                        <button type="button" class="btn" data-bs-dismiss="modal">Закрыть</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
{/if}
